import { Link } from "react-router-dom";
import { Cpu, Layers, Compass, FileCheck2, MonitorCog, ArrowUpRight, Satellite, ArrowRight, ChevronRight } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/services";
import ContourBackground from "@/components/ContourBackground";
import GridFillerCards from "@/components/GridFillerCards";
import SubServiceFillerCell from "@/components/SubServiceFillerCell";
import { padForGrid } from "@/lib/gridUtils";
import useSeo from "@/hooks/useSeo";

const STACK = [
  { t: "AutoCAD / Civil 3D", d: "Senior drafters on the latest Civil 3D releases with full template/block library mastery." },
  { t: "MicroStation / OpenRoads", d: "Bentley-stack drafters for DOT and utility ROW production." },
  { t: "Trimble Business Center", d: "GNSS post-processing, field-to-finish, and feature-coded survey CAD." },
  { t: "Carlson Survey / SurvCE", d: "Carlson-fluent drafters for survey deliverables and field codes." },
  { t: "ArcGIS / QGIS", d: "Geodatabase design, schema enforcement, and map production for survey projects." },
  { t: "Pix4D / Agisoft Metashape", d: "UAV photogrammetric processing and orthomosaic production." },
];

const ROLES = [
  { icon: <Compass className="h-5 w-5" />, t: "Senior Survey CAD Drafter", d: "8+ years, leads complex ALTA, plat, and ROW production." },
  { icon: <Layers className="h-5 w-5" />, t: "Survey CAD Lead", d: "Owns standards, blocks, and template enforcement across deliverables." },
  { icon: <FileCheck2 className="h-5 w-5" />, t: "QC Reviewer", d: "Survey-licensed reviewer for the final 3rd-tier check." },
  { icon: <Cpu className="h-5 w-5" />, t: "GIS Analyst", d: "ArcGIS Pro geodatabase, parcel fabric, and web-tile production for survey deliverables." },
  { icon: <MonitorCog className="h-5 w-5" />, t: "Drone / LiDAR Tech", d: "UAV photogrammetric and LiDAR point-cloud processing for topo and as-built scopes." },
  { icon: <Satellite className="h-5 w-5" />, t: "Field-to-Finish Specialist", d: "GNSS post-processing, feature-code clean-up and survey-grade DTM production." },
];

export default function SurveyTechies() {
  useSeo({
    title: "Survey Techies — Land Survey CAD Drafters · Tri-Axis XYZ",
    description: "Hire senior Land Survey CAD techies trained on Civil 3D, MicroStation, Trimble Business Center, Carlson and ArcGIS. ALTA, boundary, topo, plat, ROW, GIS — hourly, daily, or full-time embedded.",
  });
  return (
    <div data-testid="page-survey-techies">
      <section className="relative overflow-hidden border-b border-slate-200">
        <ContourBackground variant="light" opacity={0.55} seed={9} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Survey Techies · Land Survey CAD Drafters</div>
            <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
              Senior <span className="text-emerald-700">land-survey CAD techies</span> who plug into your firm — on day one.
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Hire by the hour, by the day, or as a full-time embedded FTE. Our survey techies
              are licensed-surveyor-supervised and trained on your standards before they touch a
              sheet. ALTA · Boundary · Topo · Plats · As-Builts · ROW · GIS.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="techies-cta-hire" className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-5 py-3">
                Hire a Survey Techie <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/services" data-testid="techies-cta-services" className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-3 bg-white">
                View Services
              </Link>
              <Link to="/cad-techies" data-testid="techies-cta-cad" className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-3 bg-white">
                Non-Survey CAD →
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="corner-marks text-slate-900 border border-slate-200 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxDQUQlMjBhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwdGVjaG5pY2FsJTIwZHJhd2luZ3xlbnwwfHx8fDE3Nzg2NjU0NzR8MA&ixlib=rb-4.1.0&q=85"
                alt="Architectural blueprint"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">01 · Survey CAD Service Lines</div>
              <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
                Eleven survey-CAD <span className="text-emerald-700">service lines</span>.
              </h2>
            </div>
            <div className="lg:col-span-5 text-slate-600">
              Click any card to jump to the service group below and see the
              sub-services we support.
            </div>
          </div>

          <div data-reveal="up" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {SERVICES.map((s, i) => (
              <a
                key={s.slug}
                href={`#svc-${s.slug}`}
                data-testid={`survey-services-card-${s.slug}`}
                className="tri-card group bg-white p-6 lg:p-7 flex flex-col gap-4 min-h-[220px] relative"
              >
                <div className="flex items-center justify-between">
                  <span className="text-3xl leading-none">{s.emoji}</span>
                  <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div>
                  <div className="display text-lg sm:text-xl font-extrabold tracking-tight text-slate-900">
                    {s.name}
                  </div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {s.short}
                  </div>
                </div>
                <div className="mt-auto flex items-center gap-1 text-sm font-semibold text-topo">
                  View sub-services{" "}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            ))}
            {/* Pad last row with marketing keyword cards so the lg:grid-cols-3 layout never has visually empty trailing cells. */}
            <GridFillerCards
              count={padForGrid(SERVICES.length)}
              kind="survey"
              startIndex={SERVICES.length}
            />
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Talent roles</div>
          <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 max-w-3xl">
            The roles you can <span className="text-emerald-700">embed today</span>.
          </h2>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {ROLES.map((r) => (
              <li key={r.t} className="bg-white p-6 lg:p-7 tri-card">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-orange-survey text-white rounded-sm">{r.icon}</span>
                <div className="display mt-4 text-lg font-extrabold tracking-tight text-slate-900">{r.t}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <DetailsSections />

      <section>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Tech stack</div>
              <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
                Fluent in every major <span className="text-emerald-700">survey-CAD stack</span>.
              </h2>
            </div>
            <div className="lg:col-span-5 text-slate-600">
              Send us your template — we&apos;ll match it within 24 hours.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {STACK.map((s, i) => (
              <div key={s.t} className="bg-white p-6 tri-card">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">{String(i + 1).padStart(2, "0")}</div>
                <div className="display mt-2 text-lg font-extrabold tracking-tight text-slate-900">{s.t}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Per-Service Detail Sections (one anchor per SERVICE) ---------- */
function DetailsSections() {
  // Order services by category so anchored detail blocks render grouped.
  const ordered = SERVICE_CATEGORIES.flatMap((cat) =>
    cat.slugs
      .map((slug) => ({ ...(SERVICES.find((x) => x.slug === slug) || {}), _category: cat.title }))
      .filter((x) => x.slug)
  );
  return (
    <section className="border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo mb-6">
          03 · Service detail
        </div>
        <div className="space-y-px bg-slate-200 border border-slate-200">
          {ordered.map((s, idx) => (
            <article
              key={s.slug}
              id={`svc-${s.slug}`}
              data-testid={`survey-detail-${s.slug}`}
              data-reveal="up"
              className="bg-white p-6 lg:p-10 scroll-mt-24"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl leading-none">{s.emoji}</span>
                    <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
                      {String(idx + 1).padStart(2, "0")} · {s._category}
                    </span>
                  </div>
                  <h3 className="display mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {s.hero || s.short}
                  </p>
                  {Array.isArray(s.deliverables) && s.deliverables.length > 0 && (
                    <div className="mt-5">
                      <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                        What we deliver
                      </div>
                      <ul className="mt-2 space-y-1.5">
                        {s.deliverables.map((d) => (
                          <li
                            key={d}
                            className="flex items-start gap-2 text-sm text-slate-700"
                          >
                            <ChevronRight className="h-4 w-4 mt-0.5 text-orange-survey shrink-0" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-6 flex flex-wrap gap-2">
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`survey-detail-page-${s.slug}`}
                      className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white"
                    >
                      Full service page <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      to="/contact"
                      data-testid={`survey-detail-cta-${s.slug}`}
                      className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2.5"
                    >
                      Request {s.name} quote <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                    Sub-services
                  </div>
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                    {(s.subpages || []).map((sp) => (
                      <li
                        key={sp.slug}
                        data-testid={`survey-subpage-${s.slug}-${sp.slug}`}
                        className="bg-white p-4 lg:p-5"
                      >
                        <Link
                          to={`/services/${s.slug}/${sp.slug}`}
                          className="display text-sm font-extrabold tracking-tight text-slate-900 hover:text-topo transition-colors"
                        >
                          {sp.name}
                        </Link>
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                          {sp.body}
                        </p>
                      </li>
                    ))}
                    {padForGrid((s.subpages || []).length, 2) === 1 && (
                      <SubServiceFillerCell
                        serviceName={s.name}
                        testid={`survey-subpage-filler-${s.slug}`}
                      />
                    )}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
